import getRoomName from '../base/config/getRoomName';
import { getBackendSafeRoomName } from '../base/util/uri';
import { JOIN_TIMELINE_KEY, describeRoomLoad, findBundleTiming, parseJoinMarker } from '../interpreter-queue/joinTimeline';
import { emitMobileTelemetryEvent } from '../mobile/navigation/telemetry';
/**
 * The room boot breadcrumbs (vrs_web_room_boot, vrs_web_room_load).
 *
 * The web room page had zero failure coverage: a web client whose conference
 * page crashed or bounced during boot left nothing (the 2026-07-31 web↔web
 * loop reached this page and then vanished without a trace). The boot
 * breadcrumb brackets the gap: if it appears without a subsequent join or
 * failure event, the boot itself died.
 *
 * Only pages with a conference report. The app's own rule for that,
 * getRoomName(), knows the /provider interpreter/captioner sign-in page and the
 * portals, which the earlier "not / and not .html" test counted as rooms
 * (2026-09-23: sha256('provider') led the room join timeline).
 *
 * @returns {RoomBootIdentity|undefined} The room and call, on a page with a conference.
 */
export function reportRoomBoot() {
    try {
        const path = typeof window === 'undefined' ? '' : window.location.pathname;
        const pathRoom = path && !path.endsWith('.html') ? getRoomName() : undefined;
        if (pathRoom) {
            let role = null;
            let storedCall = null;
            try {
                // vrs_user_role is a SESSION-ONLY key (see vrs-auth/storage.ts): it
                // is never written to localStorage, because localStorage is shared
                // across tabs and a client tab must not inherit an interpreter tab's
                // role. Reading it from localStorage therefore reported `null` on
                // every surface, every time — the one field that would have said
                // whose room boot this was. The URL carries the role too, and on the
                // interpreter path it is the more reliable of the two.
                role = new URLSearchParams(window.location.search).get('role')
                    || window.sessionStorage.getItem('vrs_user_role');
                storedCall = JSON.parse(window.localStorage.getItem('vrs_active_call') || 'null');
            }
            catch (storageError) { /* storage blocked — still worth the breadcrumb */ }
            // Bind the breadcrumb to the call. Without these the boot event could
            // not be joined to the match that preceded it or the teardown that
            // followed, so a room that died on arrival read as an unrelated event.
            // vrs_active_call is in localStorage and can outlive its call, so it
            // only counts when it names this room; a stale one would label this
            // boot with another call and hide this room's join marker.
            const activeCall = storedCall?.roomName && getBackendSafeRoomName(storedCall.roomName) === pathRoom
                ? storedCall : null;
            const pathSegments = path.split('/').filter(Boolean);
            const roomNameFromPath = decodeURIComponent(pathSegments[pathSegments.length - 1] || '');
            const roomName = activeCall?.roomName || roomNameFromPath;
            const now = Date.now();
            let marker = null;
            // The join marker was written by the tap that started this navigation
            // (home page, portal, or middleware) and is read against this room. It
            // names the call even when the stored call is stale, so the boot, the
            // load and the middleware's stage events (getStageEventCall) carry the
            // same call and the room join timeline shows one row per join; the
            // boot used to go out with no call at all.
            try {
                marker = parseJoinMarker(window.localStorage.getItem(JOIN_TIMELINE_KEY), now, roomName);
            }
            catch (markerError) { /* storage blocked — the room still identifies the boot */ }
            const callId = marker?.callId || activeCall?.callId;
            const requestId = marker?.requestId || activeCall?.requestId;
            emitMobileTelemetryEvent('vrs_web_room_boot', {
                hasActiveCall: Boolean(activeCall),
                role,
                staleActiveCall: Boolean(storedCall && !activeCall) || undefined
            }, {
                callId,
                requestId,
                roomName,
                state: 'booting'
            });
            // How long did the page itself take? The marker dates the tap; the
            // browser supplies the document and bundle timings. Together they say
            // whether a slow join is the page load or the call setup that follows.
            try {
                const perf = window.performance;
                const navigation = perf?.getEntriesByType?.('navigation')?.[0];
                const bundle = findBundleTiming(perf?.getEntriesByType?.('resource'));
                const report = describeRoomLoad({
                    marker,
                    now,
                    timeOrigin: perf?.timeOrigin || now,
                    navigation,
                    bundle
                });
                emitMobileTelemetryEvent('vrs_web_room_load', {
                    ...report,
                    role
                }, {
                    callId,
                    requestId,
                    roomName,
                    state: 'booted'
                });
            }
            catch (loadTelemetryError) { /* diagnostics must never break boot */ }
            return {
                callId,
                requestId,
                role,
                roomName,
                source: marker?.source
            };
        }
    }
    catch (bootTelemetryError) { /* diagnostics must never break boot */ }
    return undefined;
}
